import { type FlashcardSavedPublic, FlashcardsService } from "@/client"
import { AppEmptyState } from "@/components/Common/AppEmptyState"
import { HtmlContent } from "@/components/Common/HtmlContent"
import { PageShell, PageSkeleton } from "@/components/Layout"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import { Box, Button, Heading, Stack, Text } from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { FaRegBookmark } from "react-icons/fa"

export const Route = createFileRoute("/_layout/study/saved/")({
  component: StudySavedFlashcardsPage,
})

export function StudySavedFlashcardsPage() {
  const breadcrumbs = useRouteBreadcrumbs("study.index", [
    { label: "Saved Flashcards" },
  ])
  const navigate = useNavigate()

  const { data, isLoading } = useQuery({
    queryKey: ["flashcards-saved"],
    queryFn: () => FlashcardsService.listSavedFlashcards(),
  })

  if (isLoading) {
    return (
      <PageShell
        title="Saved flashcards"
        description="Review the cards you bookmarked or annotated."
        breadcrumbs={breadcrumbs}
      >
        <PageSkeleton variant="table" rows={4} />
      </PageShell>
    )
  }

  const savedCards = data?.data ?? []

  return (
    <PageShell
      title="Saved flashcards"
      description="Review the cards you bookmarked or annotated."
      breadcrumbs={breadcrumbs}
      py={{ base: 5, md: 8 }}
      contentGap={5}
    >
      {savedCards.length === 0 ? (
        <AppEmptyState
          title="No saved flashcards yet"
          description="Bookmark a card or add a note during study to keep it here."
          icon={<FaRegBookmark />}
        />
      ) : (
        <Stack gap={3}>
          {savedCards.map((savedCard) => (
            <SavedFlashcardRow
              key={savedCard.id}
              savedCard={savedCard}
              onOpen={() =>
                navigate({
                  to: "/study/saved/$cardId",
                  params: { cardId: savedCard.card_id },
                })
              }
            />
          ))}
        </Stack>
      )}
    </PageShell>
  )
}

function SavedFlashcardRow({
  savedCard,
  onOpen,
}: {
  savedCard: FlashcardSavedPublic
  onOpen: () => void
}) {
  return (
    <Box
      borderWidth={1}
      borderColor="gray.200"
      _dark={{ borderColor: "gray.700", bg: "gray.900" }}
      borderRadius="md"
      bg="white"
      p={{ base: 3, md: 4 }}
    >
      <Stack gap={3}>
        <Text fontSize="sm" fontWeight="semibold" color="fg.muted">
          {savedCard.deck_name}
        </Text>
        <Heading size="sm" lineHeight="1.35">
          <HtmlContent content={savedCard.front} />
        </Heading>

        {savedCard.note_html ? (
          <Box
            borderWidth={1}
            borderColor="gray.200"
            _dark={{ borderColor: "gray.700", bg: "gray.800" }}
            borderRadius="md"
            bg="gray.50"
            p={3}
          >
            <HtmlContent content={savedCard.note_html} color="fg.muted" />
          </Box>
        ) : null}

        <Button
          size="sm"
          type="button"
          w={{ base: "full", sm: "auto" }}
          alignSelf={{ base: "stretch", sm: "flex-end" }}
          onClick={onOpen}
        >
          Open card
        </Button>
      </Stack>
    </Box>
  )
}

export default StudySavedFlashcardsPage
